<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import Seats from '@/components/Admin/Seats.vue'
import InstructorTable from '@/components/Admin/InstructorTable.vue'
import GroupsTable from '@/components/Admin/GroupsTable.vue'
import StudentsTable from '@/components/Admin/StudentsTable.vue'
import { organizationModule } from '@/store/organization/module'

const {
    Input: PocketInput,
    Button: PocketButton,
    Icon,
    Tooltip,
} = UIKit

const route = useRoute()

const orgName = ref<null | string>(null)
const enableEditName = ref(false)
const showEditOrgNameTooltip = ref(false)
const isLoading = ref(false)
const showSuccess = ref(false)

const currentOrganization = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const isInAddStudentsState = computed(() => {
    return route.query.addStudents === 'true'
})

onMounted(async () => {
    await organizationModule.actions.fetchCurrentOrganization()
    orgName.value = currentOrganization.value?.name || null
})

const clickEditName = () => {
    enableEditName.value = true
    showEditOrgNameTooltip.value = false

    nextTick(() => {
        const nameField = document.querySelector('.organization__field input') as HTMLInputElement
        nameField.focus()
    })
}

const saveOrgName = async () => {
    showSuccess.value = false
    if (!currentOrganization.value) {
        throw new Error('saveOrgName: No current organizatoin found.')
    }
    if (!orgName.value || isLoading.value) {
        return
    }

    isLoading.value = true
    await organizationModule.actions.updateOrganization({
        organizationId: currentOrganization.value.objectId,
        name: orgName.value,
    })
    showSuccess.value = true
    setTimeout(() => showSuccess.value = false, 3000)
    enableEditName.value = false
    isLoading.value = false
}
</script>

<template>
    <div class="organization">
        <div class="organization__title">
            {{ orgName }} Settings
        </div>
        <label v-if="!enableEditName" class="organization__edit-name-label">Organization Name</label>
        <div class="organization__edit-name">
            <template v-if="!enableEditName">
                <div class="organization__name" @click="clickEditName">
                    {{ orgName }}
                </div>
                <div
                    class="organization__edit-icon"
                    @mouseover="showEditOrgNameTooltip = true"
                    @mouseout="showEditOrgNameTooltip = false"
                    @click="clickEditName"
                >
                    <Tooltip v-if="showEditOrgNameTooltip" class="organization__edit-tooltip">
                        Edit Name
                    </Tooltip>
                    <Icon
                        type="edit"
                        :tabindex="0"
                        @keyupenter="clickEditName"
                    />
                </div>
                <div v-if="showSuccess" class="organization__edit-success">
                    <Icon type="check" />New Name Saved!
                </div>
            </template>
            <template v-else>
                <PocketInput
                    v-model="orgName"
                    label="Organization Name"
                    class="organization__field"
                    @keyupenter="saveOrgName"
                />
                <PocketButton
                    :disabled="isLoading"
                    @click="saveOrgName"
                    @keyupenter="saveOrgName"
                >
                    Save
                </PocketButton>
                <PocketButton
                    type="secondary"
                    @click="enableEditName = false"
                    @keyupenter="enableEditName = false"
                >
                    Cancel
                </PocketButton>
            </template>
        </div>
        <Seats class="organization__seats" />
        <StudentsTable
            id="students"
            class="organization__students"
            :forceShowStudentsSidePanel="isInAddStudentsState"
        />
        <GroupsTable id="groups" class="organization__groups" />
        <InstructorTable id="admin-instructors" class="organization__instructors" />
    </div>
</template>

<style lang="scss" scoped>


.organization {
    &__title {
        font-size: 26px;
        line-height: 36px;
        font-weight: 600;
        margin-left: 11px;
        margin-bottom: 72px;
    }

    &__instructors {
        margin-bottom: 72px;
    }

    &__groups {
        margin-bottom: 72px;
    }

    &__edit-name-label {
        display: block;
        color: $slate-01;
        font-size: 13px;
        margin: 0 0 6px 11px;
    }

    &__edit-name {
        display: flex;
        align-items: flex-end;
        margin-bottom: 60px;

        &:hover {
            .organization__name {
                border: 1px solid $gray-divider;
                min-width: 265px;
            }

            .organization__edit-icon {
                display: flex;
            }

            .organization__edit-success {
                display: none;
            }
        }
    }

    &__field {
        margin-right: 9px;
        width: 265px;
    }

    &__edit-success {
        color: $green;
        font-size: 13px;
        margin-left: 38px;
        line-height: 18px;
        display: flex;
        align-items: center;
        align-self: center;

        svg {
            width: 12px;
            margin-right: 7px;
            margin-top: -2px;
        }
    }

    &__name {
        border: 1px solid $white;
        border-radius: 3px;
        padding: 9px 8px 9px 10px;
        font-size: 15px;
        line-height: 16px;
        height: 36px;
        box-sizing: border-box;
        outline: none;
        z-index: 2;
        margin-right: 6px;
        cursor: default;
        user-select: none;
    }

    &__edit-icon {
        cursor: pointer;
        position: relative;
        height: 36px;
        display: none;

        svg {
            width: 24px;
            height: 24px;
            display: block;
            align-self: center;
            color: $brand-blue;
        }

        &:hover svg {
            color: $brand-black;
        }
    }

    &__edit-tooltip {
        position: absolute;
        transform: translate(0, -100%);
        left: 12px;
    }

    &__students {
        margin-bottom: 72px;
    }
}
</style>
